import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Chip, Grid, Avatar, IconButton, Tooltip } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    formatMyRequestDate,
    getMyRequestAvatarBg,
    getMyRequestChipSx,
    getMyRequestDetailButtonSx,
    getMyRequestNoteButtonSx,
} from './myRoleRequests.helpers';

export interface MyRoleRequest {
    requestId: number;
    requestedRoleId: number;
    requestedRoleName: string;
    requestDate: string;
    status: number;
    statusText: string;
    requestNote: string;
    approvalDate?: string;
    approvalNotes?: string;
    rejectionReason?: string;
    assignedApproverUsername?: string;
}

interface MyRoleRequestRowProps {
    request: MyRoleRequest;
    onOpenNote: (request: MyRoleRequest) => void;
    onOpenDetail: (request: MyRoleRequest) => void;
}

const getStatusIcon = (status: number) => {
    switch (status) {
        case 0:
            return <AccessTimeIcon sx={{ fontSize: 18 }} />;
        case 1:
            return <CheckCircleIcon sx={{ fontSize: 18 }} />;
        case 2:
            return <CancelIcon sx={{ fontSize: 18 }} />;
        default:
            return undefined;
    }
};

export const MyRoleRequestRow: FC<MyRoleRequestRowProps> = ({ request, onOpenNote, onOpenDetail }) => {
    const { t } = useTranslation();

    return (
        <Box
            key={request.requestId}
            sx={{
                mb: 1.5,
                borderRadius: '8px',
                border: '1px solid #E2E8F0',
                bgcolor: 'white',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:hover': {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    borderColor: '#CBD5E1',
                },
            }}
        >
            <Grid container spacing={2} sx={{ p: 2, alignItems: 'center' }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                            sx={{
                                bgcolor: getMyRequestAvatarBg(request.status),
                                width: 36,
                                height: 36,
                            }}
                        >
                            <SecurityIcon sx={{ color: 'white', fontSize: 20 }} />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#1E293B',
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {request.requestedRoleName}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                    <Chip
                        icon={getStatusIcon(request.status)}
                        label={t(`roleApproval.status.${request.statusText.toLowerCase()}`)}
                        size="small"
                        sx={getMyRequestChipSx(request.status)}
                    />
                </Grid>

                <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                    {request.assignedApproverUsername ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ color: '#64748B', fontSize: 20 }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#1E293B',
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {request.assignedApproverUsername}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            -
                        </Typography>
                    )}
                </Grid>

                <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarTodayIcon sx={{ color: '#64748B', fontSize: 16 }} />
                        <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.875rem' }}>
                            {formatMyRequestDate(request.requestDate)}
                        </Typography>
                    </Box>
                </Grid>

                <Grid
                    size={{ xs: 6, sm: 4, md: 2 }}
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                >
                    {request.status === 0 ? (
                        <Tooltip
                            title={
                                request.requestNote
                                    ? t('roleApproval.myRoleRequests.viewRequestMessage')
                                    : t('roleApproval.myRoleRequests.noRequestNote')
                            }
                        >
                            <span>
                                <IconButton
                                    onClick={() => onOpenNote(request)}
                                    size="small"
                                    disabled={!request.requestNote}
                                    sx={getMyRequestNoteButtonSx(!!request.requestNote)}
                                >
                                    <DescriptionIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : (
                        <Tooltip title={t('roleApproval.myRoleRequests.viewDetail')}>
                            <IconButton
                                onClick={() => onOpenDetail(request)}
                                size="small"
                                sx={getMyRequestDetailButtonSx(request.status)}
                            >
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};
